/**
 * PublicCoursesService
 * Anonymous-capable catalog surface for ccms_resource_centre. The backend
 * filters by CourseVisibility lookup when no auth is present.
 * Route: api/public/courses
 */
import { BaseService } from '../base/BaseService';
export class PublicCoursesService extends BaseService {
    constructor(http, config) {
        super(http, {
            servicePath: config?.servicePath ?? PublicCoursesService.DEFAULT_SERVICE_PATH,
            basePath: PublicCoursesService.BASE_PATH,
            endpoints: config?.endpoints,
        });
    }
    async list(ministryId) {
        const response = await this.listRaw(ministryId);
        return response.data ?? [];
    }
    listRaw(ministryId) {
        const suffix = ministryId != null ? `?ministryId=${ministryId}` : '';
        return this.http.get(this.buildUrl(suffix));
    }
    async getBySlug(slug) {
        const response = await this.http.get(this.buildUrl(encodeURIComponent(slug)));
        return response.data ?? null;
    }
    /**
     * Public, anonymous-safe sessions for a course — keyed on numeric courseID.
     * The front-end resolves the slug to a course locally, then calls this with
     * `course.courseID`. Backend filters non-Public courses for anon visitors.
     */
    async getSessionsByCourseId(courseId) {
        const response = await this.getSessionsByCourseIdRaw(courseId);
        return response.data ?? [];
    }
    getSessionsByCourseIdRaw(courseId) {
        return this.http.get(this.buildUrl(`id/${courseId}/sessions`));
    }
    /** Public, anonymous-safe prerequisites for a course — see <c>getSessionsByCourseId</c>. */
    async getPrerequisitesByCourseId(courseId) {
        const response = await this.getPrerequisitesByCourseIdRaw(courseId);
        return response.data ?? [];
    }
    getPrerequisitesByCourseIdRaw(courseId) {
        return this.http.get(this.buildUrl(`id/${courseId}/prerequisites`));
    }
    /**
     * Enriched public catalog — every row carries CategoryName, MinistryName,
     * VisibilityName, StatusName, sessions[], session count + total duration.
     * Saves the public app from issuing follow-up lookup queries per card.
     */
    async listView(ministryId) {
        const response = await this.listViewRaw(ministryId);
        return response.data ?? [];
    }
    listViewRaw(ministryId) {
        const suffix = ministryId != null ? `view?ministryId=${ministryId}` : 'view';
        return this.http.get(this.buildUrl(suffix));
    }
    /** Enriched single course by id — sessions + prereqs metadata inline. */
    async getViewById(courseId) {
        const response = await this.getViewByIdRaw(courseId);
        return response.data ?? null;
    }
    getViewByIdRaw(courseId) {
        return this.http.get(this.buildUrl(`id/${courseId}/view`));
    }
    /**
     * Enriched single course by slug — same payload as {@link getViewById}
     * (sessions + required/recommended/unmet prerequisites + viewer progress +
     * canStart), resolved from the URL slug.
     */
    async getViewBySlug(slug) {
        const response = await this.getViewBySlugRaw(slug);
        return response.data ?? null;
    }
    getViewBySlugRaw(slug) {
        return this.http.get(this.buildUrl(`${encodeURIComponent(slug)}/view`));
    }
    // ─── Streaming media (HLS, transcripts, audio) ────────────────────────
    /**
     * Streaming media for one visible session. Never null when the session is
     * visible: a session without linked media comes back as "Not processed" with
     * null URLs, so the player keeps using `sessionURL`.
     * GET api/public/courses/id/{courseId}/sessions/{sessionId}/media
     */
    async getSessionMedia(courseId, sessionId) {
        const response = await this.getSessionMediaRaw(courseId, sessionId);
        return response.data ?? null;
    }
    getSessionMediaRaw(courseId, sessionId) {
        return this.http.get(this.buildUrl(`id/${courseId}/sessions/${sessionId}/media`));
    }
    /**
     * Streaming media for every visible session in a course that has linked media.
     * GET api/public/courses/id/{courseId}/sessions/media
     */
    async getCourseSessionsMedia(courseId) {
        const response = await this.getCourseSessionsMediaRaw(courseId);
        return response.data ?? [];
    }
    getCourseSessionsMediaRaw(courseId) {
        return this.http.get(this.buildUrl(`id/${courseId}/sessions/media`));
    }
    /**
     * Short-lived presigned audio download for a session. Only available when
     * the Resource Centre "audio download" setting is on (404 otherwise).
     * GET api/public/courses/id/{courseId}/sessions/{sessionId}/audio-download
     */
    async getSessionAudioDownload(courseId, sessionId) {
        const response = await this.getSessionAudioDownloadRaw(courseId, sessionId);
        return response.data ?? null;
    }
    getSessionAudioDownloadRaw(courseId, sessionId) {
        return this.http.get(this.buildUrl(`id/${courseId}/sessions/${sessionId}/audio-download`));
    }
}
PublicCoursesService.DEFAULT_SERVICE_PATH = 'resources';
PublicCoursesService.BASE_PATH = 'api/public/courses';
/**
 * Anonymous-safe categories with MinistryName, StatusName, course counts.
 * Lives at <c>/api/public/categories/view</c> — split into its own helper so
 * callers can swap path independently of the courses service.
 */
export class PublicCategoriesEnrichedService extends BaseService {
    constructor(http, config) {
        super(http, {
            servicePath: config?.servicePath ?? PublicCategoriesEnrichedService.DEFAULT_SERVICE_PATH,
            basePath: PublicCategoriesEnrichedService.BASE_PATH,
            endpoints: config?.endpoints,
        });
    }
    async listView(ministryId) {
        const response = await this.listViewRaw(ministryId);
        return response.data ?? [];
    }
    listViewRaw(ministryId) {
        const suffix = ministryId != null ? `view?ministryId=${ministryId}` : 'view';
        return this.http.get(this.buildUrl(suffix));
    }
}
PublicCategoriesEnrichedService.DEFAULT_SERVICE_PATH = 'resources';
PublicCategoriesEnrichedService.BASE_PATH = 'api/public/categories';
//# sourceMappingURL=PublicCoursesService.js.map